import { RentalContract, MilestoneDateType, ContactType } from "shared/types/rental-contracts";

export interface AnniversaryInfo {
  anniversaryDate: Date | null;
  daysUntil: number;
  isPast: boolean;
  monthsUntil: number;
}

/**
 * Calculate anniversary information for a contract
 *
 * Takes into account the last indexation date to correctly handle missed indexations.
 * If a contract was never indexed or last indexed over a year ago, the anniversary
 * stays in the past (showing the missed indexation) instead of shifting to next year.
 *
 * Belgian law: landlord can claim retroactive rent for max 3 months,
 * but the anniversary date itself stays tied to the original contract start date.
 */
export const calculateAnniversaryInfo = (
  contract: RentalContract,
  lastIndexationDate?: Date | string | null
): AnniversaryInfo => {
  const startDateMilestone = contract.contractMilestoneDates?.find(
    m => m.milestoneDateType === MilestoneDateType.START_DATE
  );

  if (!startDateMilestone || !startDateMilestone.date) {
    return {
      anniversaryDate: null,
      daysUntil: 999999,
      isPast: false,
      monthsUntil: 999
    };
  }

  const startDate = new Date(startDateMilestone.date);
  const today = new Date();

  // Create anniversary date for current year
  let anniversaryDate = new Date(
    today.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );

  // Calculate days difference
  const daysDiff = Math.floor(
    (anniversaryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Determine if the contract has been indexed recently (within the last year)
  const hasRecentIndexation = lastIndexationDate &&
    (today.getTime() - new Date(lastIndexationDate).getTime()) < (365 * 24 * 60 * 60 * 1000);

  if (daysDiff < 0) {
    if (hasRecentIndexation) {
      // Already indexed recently → shift to next year (upcoming anniversary)
      anniversaryDate = new Date(
        today.getFullYear() + 1,
        startDate.getMonth(),
        startDate.getDate()
      );
    }
    // else: missed indexation → keep past anniversary date so the user can still index
  } else if (daysDiff >= 0 && !hasRecentIndexation) {
    // Anniversary is in the future this year, but the contract has a missed indexation
    // (never indexed or last indexed > 1 year ago) → show PREVIOUS year's anniversary
    // so the user can still perform the missed indexation
    const previousYearAnniversary = new Date(
      today.getFullYear() - 1,
      startDate.getMonth(),
      startDate.getDate()
    );
    // Only shift to previous year if the contract is old enough to have had an anniversary
    const contractStartTime = startDate.getTime();
    if (previousYearAnniversary.getTime() >= contractStartTime) {
      anniversaryDate = previousYearAnniversary;
    }
  }

  const finalDaysDiff = Math.floor(
    (anniversaryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  return {
    anniversaryDate,
    daysUntil: finalDaysDiff,
    isPast: finalDaysDiff < 0,
    monthsUntil: Math.floor(finalDaysDiff / 30)
  };
};

/**
 * Get CSS class for anniversary date coloring
 */
export const getAnniversaryColorClass = (daysUntil: number): string => {
  if (daysUntil < 0) return 'text-red-600'; // Past
  if (daysUntil <= 30) return 'text-orange-600'; // Within 1 month
  if (daysUntil <= 90) return 'text-yellow-600'; // Within 3 months
  return 'text-neutral-primary'; // Further in future
};

/**
 * Get indexation status based on anniversary info
 */
export const getIndexationStatus = (info: AnniversaryInfo): string => {
  if (!info.anniversaryDate) return 'Onbekend';
  if (info.daysUntil < 0) return 'Verstreken';
  if (info.daysUntil <= 30) return 'Urgent';
  if (info.daysUntil <= 90) return 'Binnenkort';
  return 'Toekomstig';
};

/**
 * Get status chip color
 */
export const getStatusChipColor = (status: string): string => {
  switch (status) {
    case 'Verstreken':
      return 'red';
    case 'Urgent':
      return 'orange';
    case 'Binnenkort':
      return 'yellow';
    case 'Toekomstig':
      return 'moss';
    default:
      return 'neutral';
  }
};

/**
 * Format days until as readable text
 */
export const formatDaysUntil = (days: number): string => {
  if (days < 0) {
    const absDays = Math.abs(days);
    if (absDays === 1) return '1 dag geleden';
    return `${absDays} dagen geleden`;
  }
  if (days === 0) return 'Vandaag';
  if (days === 1) return 'Morgen';
  if (days <= 30) return `Over ${days} dagen`;
  if (days <= 60) return `Over ~${Math.round(days / 30)} maand`;
  return `Over ~${Math.round(days / 30)} maanden`;
};

/**
 * Get tenant name from contract
 */
export const getTenantName = (contract: RentalContract): string => {
  const tenants = contract.contractContacts?.filter(
    c => c.contactType === ContactType.TENANT
  ) || [];

  if (tenants.length === 0) return 'Onbekend';

  return tenants
    .map(t => (t as any).resource?.name || 'Onbekend')
    .join(', ');
};

/**
 * Calculate total rent from products
 */
export const calculateTotalRent = (contract: RentalContract): number => {
  if (!contract.products || contract.products.length === 0) return 0;

  const today = new Date();

  return contract.products.reduce((total, product) => {
    // Get current price (most recent price before or on today)
    const currentPrice = product.price
      ?.filter(p => new Date(p.effectiveDate) <= today)
      ?.sort((a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime())[0];

    return total + (currentPrice?.amount || 0);
  }, 0);
};
